import React, { useState, useEffect } from 'react';
import { 
  X, 
  CreditCard, 
  Copy, 
  Check, 
  ShieldCheck, 
  Upload, 
  Clock, 
  CheckCircle2,
  User,
  Scale,
  Phone,
  ExternalLink
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { formatUzPhone, isCompleteUzPhone } from '../../utils/helpers';
import { showToast } from '../common/Toast';

export default function SubscriptionModal({ isOpen, onClose, onSuccess = null, initialType = null }) {
  const { user, isAuthenticated, refreshUser, refreshSubscription } = useAuth();
  const { currentLang, t } = useLanguage();

  const isLawyer = (initialType === 'LAWYER') || (user?.role === 'LAWYER');
  const amount = isLawyer ? 99000 : 35000;
  const [cardPan, setCardPan] = useState('5614 6814 2744 1112');
  const [cardHolder, setCardHolder] = useState('JIZZAX ADVOKATLARI');

  const [copied, setCopied] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('CLICK'); // 'CLICK', 'PAYME', 'CARD'
  const [payerPhone, setPayerPhone] = useState('+998 ()');
  const [clientFullName, setClientFullName] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [pendingSubmitted, setPendingSubmitted] = useState(false);

  useEffect(() => {
    api.get('/subscriptions/pricing').then((res) => {
      if (res?.success) {
        if (res.cardPan) setCardPan(res.cardPan);
        if (res.cardHolder) setCardHolder(res.cardHolder);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.phone) {
      setPayerPhone(formatUzPhone(user.phone));
    }
  }, [user]);

  if (!isOpen) return null;

  const handleCopyCard = () => {
    navigator.clipboard.writeText(cardPan.replace(/\s+/g, ''));
    setCopied(true);
    showToast(
      currentLang === 'en' 
        ? `Card number copied: ${cardPan}` 
        : currentLang === 'ru' 
        ? `Номер карты скопирован: ${cardPan}` 
        : `Karta raqami nusxalandi: ${cardPan}`,
      'success'
    );
    setTimeout(() => setCopied(false), 2500);
  };

  const handleOpenPaymentApp = (method) => {
    setSelectedMethod(method);
    const cleanPan = cardPan.replace(/\s+/g, '');
    navigator.clipboard.writeText(cleanPan);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);

    if (method === 'CLICK') {
      showToast(
        currentLang === 'en'
          ? `Card ${cardPan} copied! Opening Click...`
          : currentLang === 'ru'
          ? `Карта ${cardPan} скопирована! Открываем Click...`
          : `Karta raqami nusxalandi: ${cardPan}. Click ochilmoqda...`,
        'info'
      );
      window.open(`https://my.click.uz/clickp2p/${cleanPan}`, '_blank');
    } else if (method === 'PAYME') {
      showToast(
        currentLang === 'en'
          ? `Card ${cardPan} copied! Opening Payme...`
          : currentLang === 'ru'
          ? `Карта ${cardPan} скопирована! Открываем Payme...`
          : `Karta raqami nusxalandi: ${cardPan}. Payme ilovasida 'O‘tkazmalar'ga o‘ting.`,
        'info'
      );
      window.open(`https://payme.uz/p2p/${cleanPan}`, '_blank');
    }
  };

  const handlePayerPhoneChange = (e) => {
    const formatted = formatUzPhone(e.target.value, payerPhone);
    setPayerPhone(formatted);
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      showToast(
        currentLang === 'en'
          ? 'Only JPG, PNG images or PDF receipts are accepted'
          : currentLang === 'ru'
          ? 'Принимаются только изображения (JPG, PNG) или PDF'
          : 'Faqat rasm (JPG, PNG) yoki PDF chek qabul qilinadi',
        'error'
      );
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'receipts');

    setUploadingReceipt(true);
    try {
      const res = await api.postFormData('/upload', formData);
      if (res?.success && res?.url) {
        setReceiptUrl(res.url);
        setReceiptFile(file);
        showToast(
          currentLang === 'en'
            ? 'Receipt attached successfully!'
            : currentLang === 'ru'
            ? 'Чек успешно прикреплен!'
            : 'To‘lov cheki muvaffaqiyatli biriktirildi!',
          'success'
        );
      }
    } catch (err) {
      showToast(err.message || 'Chekni yuklashda xatolik', 'error');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleConfirmPayment = async (e) => {
    e.preventDefault();
    if (!isCompleteUzPhone(payerPhone)) {
      showToast(
        currentLang === 'en'
          ? 'Please enter your phone number: +998 (90) 123-45-67'
          : currentLang === 'ru'
          ? 'Введите номер телефона плательщика: +998 (90) 123-45-67'
          : 'To‘lovchi telefon raqamini to‘liq kiriting: +998 (90) 123-45-67',
        'error'
      );
      return;
    }

    if (!receiptUrl) {
      showToast(
        currentLang === 'en'
          ? 'Payment receipt or screenshot is required! Please attach it first.'
          : currentLang === 'ru'
          ? 'Чек или скриншот оплаты обязателен! Пожалуйста, сначала прикрепите его.'
          : 'To‘lov cheki yoki skrinshotini yuklash majburiy! Iltimos, avval chekni yuklang.',
        'error'
      );
      return;
    }

    setSubmitting(true);
    try {
      const combinedNotes = `${payerPhone}${notes.trim() ? ` | ${notes.trim()}` : ''}`;
      const payload = {
        paymentMethod: selectedMethod,
        phone: payerPhone,
        payerPhone,
        fullName: clientFullName.trim() || undefined,
        password: clientPassword.trim() || undefined,
        receiptUrl,
        notes: combinedNotes,
      };
      const res = await api.post('/subscriptions/confirm-payment', payload);
      if (res?.success) {
        if (refreshSubscription) await refreshSubscription();

        setPendingSubmitted(true);
        showToast(
          currentLang === 'en'
            ? 'Payment submitted! Administrator will verify and activate your access.'
            : currentLang === 'ru'
            ? 'Платеж отправлен на проверку! Администратор активирует подписку после проверки чека.'
            : 'To‘lov cheki tekshiruvga yuborildi! Administrator tasdiqlashi bilan obunangiz faollashadi.',
          'info'
        );

        // Only call onSuccess if status is actually ACTIVE
        if (res?.subscription?.status === 'ACTIVE' || res?.subscription?.isFreeExempt) {
          if (res?.token) {
            localStorage.setItem('advokat_token', res.token);
            if (refreshUser) await refreshUser();
          }
          if (onSuccess) onSuccess(res.subscription);
        }
      }
    } catch (err) {
      showToast(err.message || 'To‘lovni tasdiqlashda xatolik', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto p-2 sm:p-4 flex justify-center items-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity" 
        onClick={onClose}
      />

      {/* Modal Container: responsive scale, strictly constrained to viewport */}
      <div className="relative w-full max-w-lg my-auto max-h-[94vh] flex flex-col bg-[#070c16] border border-[#c59b27]/50 rounded-2xl shadow-2xl shadow-black/95 overflow-hidden z-10 text-slate-200 animate-in zoom-in-95 duration-200">
        
        {/* Top Gold Bar */}
        <div className="h-1 w-full bg-gradient-to-r from-amber-600 via-[#c59b27] to-amber-600 shrink-0" />

        {/* Sticky Header */}
        <div className="shrink-0 flex items-center justify-between px-4 sm:px-5 py-3 sm:py-3.5 border-b border-[#162238] bg-[#070c16]">
          <div className="flex items-center space-x-2.5 min-w-0 pr-2">
            <div className="w-8 h-8 rounded-lg bg-[#142034] border border-[#c59b27]/40 flex items-center justify-center text-[#c59b27] shrink-0">
              {isLawyer ? <Scale className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
            </div>
            <div className="min-w-0">
              <span className="text-[9.5px] uppercase tracking-wider text-[#c59b27] font-semibold block truncate">
                {isLawyer 
                  ? t('sub_modal_badge_lawyer', 'Advokat Shaxsiy Kabineti')
                  : t('sub_modal_badge_client', 'Mijoz Shaxsiy Kabineti')}
              </span>
              <h3 className="font-serif text-sm sm:text-base font-bold text-slate-100 truncate">
                {t('sub_modal_title', '30 Kunlik Oylik Obuna')}
              </h3>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-[#121c2e] hover:bg-[#1a2840] border border-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer shrink-0 ml-2"
            title="Yopish"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Content: If submitted, show verification view */}
        {pendingSubmitted ? (
          <div className="p-6 text-center space-y-4 my-auto">
            <div className="w-16 h-16 rounded-full bg-amber-500/20 border border-amber-500/50 flex items-center justify-center text-amber-400 mx-auto animate-pulse">
              <Clock className="w-8 h-8" />
            </div>
            <h4 className="font-serif text-lg font-bold text-slate-100">
              {currentLang === 'en'
                ? 'Payment Submitted for Verification'
                : currentLang === 'ru'
                ? 'Платеж принят на проверку'
                : 'To‘lov Qabul Qilindi va Tekshiruvga Yuborildi!'}
            </h4>
            <div className="bg-[#0b1220] border border-[#1b2b46] rounded-xl p-4 text-xs text-slate-300 max-w-sm mx-auto leading-relaxed text-left space-y-2.5">
              <div className="flex items-start space-x-2.5">
                <ShieldCheck className="w-4 h-4 text-[#c59b27] shrink-0 mt-0.5" />
                <span className="leading-relaxed">
                  {currentLang === 'en'
                    ? 'Our administrator will verify your payment receipt (usually within 5–15 minutes). Once verified, your 30-day access will be fully activated.'
                    : currentLang === 'ru'
                    ? 'Администратор проверит ваш платеж (обычно в течение 5–15 минут). После проверки аккаунт будет активирован на 30 дней.'
                    : 'Administratorimiz to‘lovingizni (Click / Payme o‘tkazmasini) tekshirib tasdiqlashi bilan (5–15 daqiqa ichida) akkauntingiz 30 kunga to‘liq faollashadi.'}
                </span>
              </div>
              <div className="text-[11px] text-slate-400 font-mono pt-2 border-t border-slate-800/80 flex items-center justify-between">
                <span>To‘lovchi: <strong>{payerPhone}</strong></span>
                <span>Summa: <strong>{amount.toLocaleString('uz-UZ')} UZS</strong></span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] text-slate-950 font-bold text-xs shadow-lg transition-colors cursor-pointer"
            >
              {currentLang === 'en' ? 'Close' : currentLang === 'ru' ? 'Понятно (Закрыть)' : 'Tushundim (Yopish)'}
            </button>
          </div>
        ) : (
          <form onSubmit={handleConfirmPayment} className="flex flex-col flex-1 min-h-0">
            {/* Scrollable inputs area */}
            <div className="flex-1 overflow-y-auto p-3.5 sm:p-5 space-y-3.5">
              
              {/* Price Box */}
              <div className="bg-[#0b1220] border border-[#c59b27]/40 rounded-xl p-3 sm:p-3.5 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2.5">
                <div className="min-w-0">
                  <span className="text-[10px] sm:text-[10.5px] text-slate-400 block mb-0.5">
                    {t('sub_price_label', 'Oylik to‘lov summasi:')}
                  </span>
                  <span className="font-serif text-xl sm:text-2xl font-bold text-[#c59b27] tracking-tight block">
                    {amount.toLocaleString('uz-UZ')} {currentLang === 'ru' ? 'сум' : 'UZS'}
                  </span>
                  <span className="text-[10px] text-slate-400 block mt-0.5">
                    {isLawyer 
                      ? t('sub_tariff_lawyer', '(Advokat tarifi • 99 000 so‘m/oy)') 
                      : t('sub_tariff_client', '(Mijoz tarifi • 35 000 so‘m/oy)')}
                  </span>
                </div>

                <div className="px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold flex items-center shrink-0 whitespace-nowrap">
                  <Clock className="w-3 h-3 mr-1 shrink-0" />
                  <span>{t('sub_period_badge', '30 kunlik muddat')}</span>
                </div>
              </div>

              {/* STEP 1: ONE-CLICK DIRECT APP OPENING (Click & Payme) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[10.5px] uppercase tracking-wider font-bold text-[#c59b27] flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-[#c59b27]/20 border border-[#c59b27] text-[#c59b27] flex items-center justify-center text-[9px]">1</span>
                    <span>{t('sub_step1_title', '1-Qadam: Click yoki Payme orqali to‘lash')}</span>
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  {/* Click Up Action Button */}
                  <button
                    type="button"
                    onClick={() => handleOpenPaymentApp('CLICK')}
                    className="flex flex-col items-center justify-center p-2.5 sm:p-3 rounded-xl bg-gradient-to-b from-[#0a2342] to-[#07172b] hover:from-[#0f325e] hover:to-[#0a2342] border border-[#0073ff]/60 hover:border-[#0073ff] transition-all group cursor-pointer shadow-md text-center"
                  >
                    <div className="px-2.5 py-0.5 rounded-md bg-[#0073ff] text-white font-extrabold text-[11px] tracking-wider shadow-sm mb-1 group-hover:scale-105 transition-transform">
                      CLICK
                    </div>
                    <span className="text-[11.5px] font-bold text-white block truncate max-w-full">
                      {t('sub_click_btn', 'Click Up orqali')}
                    </span>
                    <span className="text-[9.5px] text-blue-300 flex items-center gap-1 mt-0.5 whitespace-nowrap">
                      {t('sub_open_app', 'Ilovani ochish')} <ExternalLink className="w-2.5 h-2.5" />
                    </span>
                  </button>

                  {/* Payme Action Button */}
                  <button
                    type="button"
                    onClick={() => handleOpenPaymentApp('PAYME')}
                    className="flex flex-col items-center justify-center p-2.5 sm:p-3 rounded-xl bg-gradient-to-b from-[#062924] to-[#051c18] hover:from-[#0a3a33] hover:to-[#062924] border border-[#00b4d8]/60 hover:border-[#00b4d8] transition-all group cursor-pointer shadow-md text-center"
                  >
                    <div className="px-2.5 py-0.5 rounded-md bg-[#00b4d8] text-slate-950 font-extrabold text-[11px] tracking-wider shadow-sm mb-1 group-hover:scale-105 transition-transform">
                      PAYME
                    </div>
                    <span className="text-[11.5px] font-bold text-white block truncate max-w-full">
                      {t('sub_payme_btn', 'Payme orqali')}
                    </span>
                    <span className="text-[9.5px] text-teal-300 flex items-center gap-1 mt-0.5 whitespace-nowrap">
                      {t('sub_open_app', 'Ilovani ochish')} <ExternalLink className="w-2.5 h-2.5" />
                    </span>
                  </button>
                </div>
              </div>

              {/* Official Card Information */}
              <div className="bg-[#0a0f1b] border border-[#1b2b46] rounded-xl p-3 space-y-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold block">
                  {t('sub_card_label', 'To‘lov o‘tkaziladigan rasmiy karta:')}
                </span>

                <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 p-2.5 rounded-lg bg-[#0e172a] border border-[#1e2f4c]">
                  <div className="flex items-center space-x-2.5 min-w-0">
                    <CreditCard className="w-4 h-4 text-[#c59b27] shrink-0" />
                    <div className="min-w-0">
                      <span className="font-mono text-sm sm:text-base font-bold text-slate-100 tracking-wider block whitespace-nowrap">
                        {cardPan}
                      </span>
                      <span className="text-[9.5px] text-slate-400 block truncate">
                        {cardHolder}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleCopyCard}
                    className="px-2.5 py-1.5 rounded bg-[#17243c] hover:bg-[#203254] text-xs font-semibold text-[#c59b27] flex items-center transition-colors cursor-pointer shrink-0 ml-auto sm:ml-2"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3 h-3 mr-1 text-emerald-400" />
                        <span className="text-emerald-400 text-[10.5px] font-medium">{t('sub_copied', 'Nusxalandi')}</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        <span className="text-[10.5px] font-medium">{t('sub_copy_card', 'Nusxalash')}</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="text-[10px] text-slate-400 flex items-start space-x-1.5 pt-0.5">
                  <ShieldCheck className="w-3 h-3 text-emerald-400 shrink-0 mt-0.5" />
                  <span className="leading-relaxed">
                    {t('sub_auto_copy_hint', 'Click yoki Payme orqali to‘lov qilganda karta raqami avtomatik nusxalanadi.')}
                  </span>
                </div>
              </div>

              {/* STEP 2: PHONE NUMBER & CONFIRMATION */}
              <div className="space-y-2 pt-1 border-t border-[#162238]">
                <div className="flex items-center justify-between">
                  <label className="text-[10.5px] uppercase tracking-wider font-bold text-[#c59b27] flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-[#c59b27]/20 border border-[#c59b27] text-[#c59b27] flex items-center justify-center text-[9px]">2</span>
                    <span>{t('sub_step2_title', '2-Qadam: Telefon raqam va Tasdiqlash')}</span>
                  </label>
                  {isCompleteUzPhone(payerPhone) && (
                    <span className="text-[10px] text-emerald-400 font-semibold flex items-center gap-0.5">
                      <Check className="w-3 h-3" /> {t('sub_complete_badge', 'To‘liq')}
                    </span>
                  )}
                </div>

                {/* PHONE INPUT */}
                <div>
                  <label className="text-[10px] text-slate-300 block mb-1">
                    {t('sub_phone_label', 'To‘lovchi Telefon Raqami (Click / Payme) *')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-[#c59b27]">
                      <Phone className="w-3.5 h-3.5" />
                    </div>
                    <input
                      type="tel"
                      inputMode="tel"
                      required
                      placeholder="+998 (90) 123-45-67"
                      value={payerPhone}
                      onFocus={() => {
                        if (!payerPhone || payerPhone === '') setPayerPhone('+998 ()');
                      }}
                      onChange={handlePayerPhoneChange}
                      className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#0a0f1b] border border-[#1b2b46] focus:border-[#c59b27] text-xs text-slate-100 font-mono focus:outline-none transition-colors"
                    />
                  </div>
                  <p className="text-[9.5px] text-slate-400 mt-1 font-mono">
                    {t('sub_phone_hint', 'Faqat raqamlar kiritiladi: +998 (97) 325-07-70')}
                  </p>
                </div>

                {/* Guest Client Details (Name & Password for automatic account setup) */}
                {!isAuthenticated && (
                  <div className="space-y-2 pt-1 border-t border-[#162238]/60">
                    <div>
                      <label className="text-[10px] text-slate-300 block mb-1">
                        {t('sub_name_label', 'Ism va familiyangiz (Ixtiyoriy)')}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500">
                          <User className="w-3.5 h-3.5" />
                        </div>
                        <input
                          type="text"
                          placeholder={t('sub_name_placeholder', 'Masalan: Ali Valiyev')}
                          value={clientFullName}
                          onChange={(e) => setClientFullName(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#0a0f1b] border border-[#1b2b46] focus:border-[#c59b27] text-xs text-slate-100 focus:outline-none transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] text-slate-300 block mb-1">
                        {t('sub_password_label', 'Parol yaratish (Ixtiyoriy - kelgusida kirish uchun)')}
                      </label>
                      <input
                        type="password"
                        placeholder={t('sub_password_placeholder', 'Kamida 6 ta belgi')}
                        value={clientPassword}
                        onChange={(e) => setClientPassword(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-[#0a0f1b] border border-[#1b2b46] focus:border-[#c59b27] text-xs text-slate-100 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                )}

                {/* Upload Receipt (Mandatory) */}
                <div>
                  <label className="text-[10px] text-slate-300 block mb-1">
                    {t('sub_receipt_label', 'To‘lov cheki yoki skrinshot')} <span className="text-amber-400 font-bold">* ({t('sub_receipt_mandatory', 'Majburiy')})</span>:
                  </label>
                  <label className={`flex items-center justify-center p-2.5 rounded-lg bg-[#0a0f1b] border border-dashed transition-colors cursor-pointer ${receiptUrl ? 'border-emerald-500/80 bg-emerald-950/20' : 'border-[#1f3150] hover:border-[#c59b27]'}`}>
                    <Upload className={`w-3.5 h-3.5 mr-2 shrink-0 ${receiptUrl ? 'text-emerald-400' : 'text-[#c59b27]'}`} />
                    <span className="truncate max-w-[280px] text-xs text-slate-300">
                      {uploadingReceipt ? t('sub_receipt_uploading', 'Yuklanmoqda...') : receiptFile ? receiptFile.name : t('sub_receipt_select', 'To‘lov chekini (rasm yoki PDF) yuklang *')}
                    </span>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleReceiptUpload}
                      className="hidden"
                      required
                    />
                  </label>
                  {receiptUrl ? (
                    <span className="text-[10px] text-emerald-400 block mt-1 font-semibold flex items-center gap-1">
                      <Check className="w-3 h-3 text-emerald-400" /> {t('sub_receipt_attached', 'To‘lov cheki biriktirildi')}
                    </span>
                  ) : (
                    <span className="text-[9.5px] text-amber-400/90 block mt-1">
                      {t('sub_receipt_hint', 'Click yoki Payme to‘lov kvitansiyasining rasmini biriktiring')}
                    </span>
                  )}
                </div>

                {/* Optional Transaction ID / Note */}
                <div>
                  <input
                    type="text"
                    placeholder={t('sub_notes_placeholder', 'Tranzaksiya ID si yoki chek kodi (ixtiyoriy)...')}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-1.5 rounded-lg bg-[#0a0f1b] border border-[#162238] text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-[#c59b27] transition-colors"
                  />
                </div>
              </div>

            </div>

            {/* STICKY BOTTOM ACTION FOOTER: ALWAYS VISIBLE, NEVER CUT OFF */}
            <div className="shrink-0 p-3 sm:px-5 sm:py-3.5 bg-[#070c16] border-t border-[#162238] flex items-center justify-between gap-2.5">
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 sm:px-4 py-2.5 rounded-lg border border-slate-700 hover:border-slate-500 text-xs font-semibold text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0"
              >
                {t('sub_btn_cancel', 'Bekor qilish')}
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-2.5 px-3 sm:px-4 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] disabled:opacity-50 text-slate-950 font-bold text-xs shadow-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer leading-normal text-center"
              >
                {submitting ? (
                  <span>{t('sub_btn_verifying', 'Tekshirilmoqda...')}</span>
                ) : (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 text-slate-950 shrink-0" />
                    <span>{t('sub_btn_confirm', 'To‘lov qildim (Tasdiqlash)')}</span>
                  </>
                )}
              </button>
            </div>

          </form>
        )}

      </div>
    </div>
  );
}
